import React from 'react'
import clsx from 'clsx'
import { Table, Row } from '@tanstack/react-table'
import { useVirtual } from 'react-virtual'

import LoadingSpinner from '../../../../atoms/LoadingSpinner'
import RowComponent, { RowComponentProps } from '../RowComponent'

type TableBodyComponentProps<T> = {
  table: Table<T>
  loaded: boolean
  message: string
  BodyWrapper: typeof React.Fragment
  expanded: Record<string, boolean>
  targetID: string
  targetRef: React.MutableRefObject<any>
  target: Partial<T>
  stickyHeader: boolean
  wrapperRef: React.MutableRefObject<any>
  estimatedRowHeight?: number
  enableCustomContextMenu?: boolean
} & Omit<
  RowComponentProps<T>,
  'row' | 'index' | 'rowKey' | 'isExpanded' | 'isTarget' | 'blinkRow'
>

const TableBodyComponent = <T extends unknown>({
  table,
  loaded,
  message,
  BodyWrapper,
  expanded,
  targetID,
  targetRef,
  target,
  stickyHeader,
  wrapperRef,
  estimatedRowHeight = 36,
  ...props
}: TableBodyComponentProps<T>) => {
  const parentRef = React.useRef<HTMLTableSectionElement>(null)
  const [scrolled, setScrolled] = React.useState(false)

  const numCells = table.getHeaderGroups()[0]?.headers.length ?? 1
  const rows = table.getRowModel().rows
  const numRows = rows.length

  const estimateSize = React.useCallback(
    () => estimatedRowHeight,
    [estimatedRowHeight]
  )
  const rowVirtualizer = useVirtual({
    parentRef: wrapperRef,
    size: numRows,
    overscan: stickyHeader ? 20 : numRows,
    estimateSize
  })
  const { virtualItems: virtualRows, totalSize, scrollToIndex } = rowVirtualizer

  const paddingTop = stickyHeader
    ? virtualRows.length > 0
      ? virtualRows?.[0]?.start || 0
      : 0
    : 0
  const paddingBottom = stickyHeader
    ? virtualRows.length > 0
      ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0)
      : 0
    : 0

  // Reset scroll on id change
  React.useEffect(() => {
    setScrolled(false)
  }, [targetID])

  // Scroll to targeted row
  React.useEffect(() => {
    if (scrolled) {
      return
    }

    if (!targetRef || !targetID) {
      return
    }

    const targetRow = virtualRows.find(virtualRow => {
      const i = virtualRow.index
      const row = rows[i] as Row<T>
      // @ts-ignore
      return row.original?.id === targetID
    })

    if (!targetRow) {
      return
    }

    scrollToIndex(targetRow.index, { align: 'start' })
    setScrolled(true)
  }, [scrolled, virtualRows, rows, targetID, targetRef, scrollToIndex])

  if (!loaded) {
    return (
      <tbody className={clsx('tbody', stickyHeader && 'sticky-tbody')}>
        <tr className='table-row'>
          <td colSpan={numCells}>
            <div className='no-data'>
              <LoadingSpinner />
            </div>
          </td>
        </tr>
      </tbody>
    )
  }

  if (numRows === 0) {
    return (
      <tbody className={clsx('tbody', stickyHeader && 'sticky-tbody')}>
        <tr className='table-row'>
          <td colSpan={numCells}>
            <div className='no-data'>{message}</div>
          </td>
        </tr>
      </tbody>
    )
  }

  return (
    <tbody
      ref={parentRef}
      className={clsx('tbody', stickyHeader && 'sticky-tbody')}
    >
      {paddingTop > 0 && (
        <tr>
          <td style={{ height: `${paddingTop}px` }} />
        </tr>
      )}

      <BodyWrapper>
        {virtualRows.map(virtualRow => {
          const i = virtualRow.index
          const row = rows[i] as Row<T>

          // @ts-ignore
          const rowId: string = row.original?.id
          const rowKey = rowId ?? i

          let isExpanded = false
          if (rowId in expanded) {
            isExpanded = Boolean(expanded[rowId])
          }

          const isTarget = rowId === targetID && targetRef
          // @ts-ignore
          const blinkRow = target && rowId === target.id

          return (
            <RowComponent
              key={row.id}
              // @ts-ignore
              row={row}
              index={i}
              rowKey={rowKey}
              isExpanded={isExpanded}
              isTarget={isTarget}
              blinkRow={blinkRow}
              targetRef={targetRef}
              {...props}
            />
          )
        })}
      </BodyWrapper>

      {paddingBottom > 0 && (
        <tr>
          <td style={{ height: `${paddingBottom}px` }} />
        </tr>
      )}
    </tbody>
  )
}

export default TableBodyComponent
